/**
 * storyPresentationApi — REST fetch function for the presentation endpoint.
 *
 * Toggle via REACT_APP_STORY_PRESENTATION_API env flag:
 *   unset / "live" → real HTTP calls
 *   "mock"         → offline fixture from storyPresentationApi.mock.ts
 *
 * Consumed by the useStoryPresentation hook.
 */
import { isMockApiFlag } from "../apiMode";
import { API_ENDPOINTS, apiService } from "../api";
import {
  mockDownloadPresentation,
  mockFetchPresentation,
  mockPrerenderPresentation,
} from "./storyPresentationApi.mock";
import type {
  PresentationDownloadFormatApi,
  PresentationDownloadRequestApi,
  PresentationDownloadResponseApi,
  PresentationPlanReadApi,
} from "./storyPresentation.types";

const isMock = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_STORY_PRESENTATION_API);

/**
 * Fetch the story's live, continuously-merged slide plan. The backend
 * auto-derives/merges this from canvas items on every read — there is no
 * separate generate/create step.
 */
export async function fetchPresentation(
  workspaceId: string,
  storyId: string,
): Promise<PresentationPlanReadApi> {
  if (isMock()) return mockFetchPresentation(workspaceId, storyId);

  return apiService.get<PresentationPlanReadApi>(
    API_ENDPOINTS.stories.presentation(workspaceId, storyId),
  );
}

/**
 * PATCH .../presentation/slides/:slideId — sets (string) or clears (null) the
 * closing slide's title. Returns the whole plan, like the GET.
 */
export async function updateSlideTitle(
  workspaceId: string,
  storyId: string,
  slideId: string,
  title: string | null,
): Promise<PresentationPlanReadApi> {
  return apiService.patch<PresentationPlanReadApi>(
    API_ENDPOINTS.stories.presentationSlide(workspaceId, storyId, slideId),
    { title },
  );
}

/**
 * POST .../artifacts/presentation/download — renders the live plan to
 * pptx/pdf, uploads it, records a story_artifacts row, and returns a
 * signed download link in one call (no separate generate step). The backend
 * caches renders by content: unchanged content, theme and format return the
 * same artifact (re-signed) in well under a second instead of re-rendering.
 */
export async function downloadPresentation(
  workspaceId: string,
  storyId: string,
  format: PresentationDownloadFormatApi,
  options: {
    theme?: string;
    includePreview?: boolean;
    /** Caps how many pages get rasterised/uploaded (e.g. the delivery
     *  attachment-preview card only ever shows page 1) — omitted means "all". */
    previewPageLimit?: number;
  } = {},
): Promise<PresentationDownloadResponseApi> {
  if (isMock()) return mockDownloadPresentation(workspaceId, storyId, format);

  const body: PresentationDownloadRequestApi = {
    format,
    ...(options.theme ? { theme: options.theme } : {}),
    ...(options.includePreview ? { include_preview: true } : {}),
    ...(options.previewPageLimit !== undefined
      ? { preview_page_limit: options.previewPageLimit }
      : {}),
  };
  return apiService.post<PresentationDownloadResponseApi>(
    API_ENDPOINTS.stories.artifactPresentationDownload(workspaceId, storyId),
    body,
  );
}

/**
 * POST .../artifacts/presentation/prerender — asks the backend to render the
 * Presentation tab's deck (default theme, pptx, every page) in the
 * background, so it is already cached when the tab is opened. Resolves once
 * the render is queued (202, no body), not when it finishes.
 */
export async function prerenderPresentation(
  workspaceId: string,
  storyId: string,
): Promise<void> {
  if (isMock()) return mockPrerenderPresentation(workspaceId, storyId);

  await apiService.post<void>(
    API_ENDPOINTS.stories.artifactPresentationPrerender(workspaceId, storyId),
    {},
  );
}
